// Money settings in rupees, not paise (spec/03 §9, spec/13 §13).
//
// A setting declared `unit: 'money'` — or a field inside one carrying `x-clavian-unit: money` — is
// integer minor units on the wire, like every amount (CLAUDE.md, spec/06 §2). The screen shows and
// takes it in the major unit of the settings view's currency (`scope.currency`): "₹" beside "20,000",
// "Default: ₹20,000.00", "Reset to inherited: ₹100.00". Nothing here is a float: a typed amount is
// split at the decimal point and padded, so "19.99" is 1999 exactly, and a stored amount is formatted
// by the venue formatter from its integer (`@clavian/i18n`).

import { SETTING_UNIT_KEYWORD } from '@clavian/contracts/constants';
import { minorUnitDigits, type VenueFormatter } from '@clavian/i18n';

import type { JsonSchema } from './schema';

/** What a screen needs to draw a money setting: its currency, and the formatter's words for an amount. */
export interface SettingMoney {
  readonly currency: string;
  /** How many minor units make one major unit: 2 for INR. */
  readonly digits: number;
  /** The currency's symbol as the formatter writes it: `₹`. */
  readonly symbol: string;
  /** An amount in a sentence: 2000000 → `₹20,000.00`. */
  readonly format: (minor: number) => string;
  /** An amount in a money field, without the symbol: 2000000 → `20,000`, 9950 → `99.50`. */
  readonly formatInput: (minor: number) => string;
}

/** The money words of one screen, from the console's formatter and the settings view's currency. */
export function settingMoney(formatter: VenueFormatter, currency: string): SettingMoney {
  const digits = minorUnitDigits(currency);
  const symbol = formatter.moneyParts({ amount: 0, currency }).find((part) => part.type === 'currency')?.value ?? currency;
  return {
    currency,
    digits,
    symbol,
    format: (minor) => formatter.money({ amount: minor, currency }),
    formatInput: (minor) => {
      const parts = formatter.moneyParts({ amount: minor, currency });
      const fraction = parts.find((part) => part.type === 'fraction')?.value ?? '';
      // A whole amount reads as one ("20,000"), any other keeps every fraction digit ("99.50").
      const whole = /^0*$/.test(fraction);
      return parts
        .filter((part) => part.type !== 'currency' && part.type !== 'literal')
        .filter((part) => !whole || (part.type !== 'decimal' && part.type !== 'fraction'))
        .map((part) => part.value)
        .join('');
    },
  };
}

/** Does this (resolved) schema say its value is money? Only an integer can be. */
export function isMoneySchema(schema: JsonSchema): boolean {
  return schema[SETTING_UNIT_KEYWORD] === 'money' && schema['type'] === 'integer';
}

export type MajorAmountParse =
  | { readonly ok: true; readonly minor: number }
  | { readonly ok: false; readonly reason: 'invalid' | 'fraction' | 'whole' };

/**
 * A typed amount in the major unit → integer minor units, exactly. Takes "20000", "20,000",
 * "20,00,000" (Indian grouping), "99.5" and "99.50", with the currency symbol or spaces around it;
 * refuses more fraction digits than the currency has ("1.234" in INR), a negative unless `negative`
 * allows it, and anything that is not a number. No float is involved: "19.99" is 19 × 100 + 99.
 */
export function parseMajorAmount(input: string, money: Pick<SettingMoney, 'digits' | 'symbol'>, negative = false): MajorAmountParse {
  let text = input.trim();
  if (money.symbol !== '' && text.startsWith(money.symbol)) text = text.slice(money.symbol.length).trim();
  let sign = 1;
  if (text.startsWith('-')) {
    if (!negative) return { ok: false, reason: 'invalid' };
    sign = -1;
    text = text.slice(1).trim();
  }
  // Digits, optionally grouped by commas between digits, then an optional fraction.
  const match = /^(\d+(?:,\d+)*)(?:\.(\d+))?$/.exec(text);
  if (match === null) return { ok: false, reason: 'invalid' };
  const whole = (match[1] ?? '').replaceAll(',', '');
  const fraction = match[2] ?? '';
  if (fraction.length > 0 && money.digits === 0) return { ok: false, reason: 'whole' };
  if (fraction.length > money.digits) return { ok: false, reason: 'fraction' };
  const minor = Number(whole) * 10 ** money.digits + Number(fraction.padEnd(money.digits, '0') || '0');
  if (!Number.isSafeInteger(minor)) return { ok: false, reason: 'invalid' };
  // `-0` is 0: a negative sign on nothing is not an amount worth keeping.
  return { ok: true, minor: minor === 0 ? 0 : sign * minor };
}
